import { AdvancedMarker, Map as GoogleMapComponent, MapCameraChangedEvent, MapMouseEvent, useMap } from '@vis.gl/react-google-maps';
import { MapConfig, Marker, Position } from '../../../domain/entities/map.types';
import { memo, useEffect } from 'react';

import ClusterController from '../markers/ClusterController';
import ClusteringGridOverlay from '../markers/ClusteringGridOverlay';
import { HandleMapInteractions } from '@/map/application/useCases/HandleMapInteractions';
import MapControls from './MapControls';
import { useMapInteractions } from '@/map/presentation/components/RaptorMap/hooks/useMapInteractions';
import { useMapState } from '@/map/presentation/components/RaptorMap/hooks/useMapState';

// Map configuration
export const MAP_CONFIG = {
  DEFAULT_CENTER: { lat: 40.7128, lng: -74.006 }, // New York City
  DEFAULT_ZOOM: 10,
  MIN_ZOOM: 3,
  DEFAULT_MAP_TYPE: 'roadmap' as const,
  DEFAULT_GESTURE_HANDLING: 'greedy' as const,
  DEFAULT_DISABLE_UI: true,
  MAP_ID: 'db1c7e59be1928cbca893ecb'
} as const;

export interface RaptorMapProps {
  config?: Partial<MapConfig>;
  style?: React.CSSProperties;
  onZoomChanged?: (zoom: number) => void;
  onCenterChanged?: (center: Position) => void;
  onClick?: (e: MapMouseEvent) => void;
  onContextmenu?: (e: MapMouseEvent) => void;
  children?: React.ReactNode;
  setMapInstance?: (map: google.maps.Map) => void;
  markers?: Marker[];
  enableClustering?: boolean;
  handleMapInteractions: HandleMapInteractions;
  showControls?: boolean;
  clusteringOptions?: import('supercluster').Options<import('geojson').GeoJsonProperties, Record<string, unknown>>;
  showGrid?: boolean;
}

const DEFAULT_STYLE = { width: '100%', height: '100%' } as const;
const MAP_ID = 'db1c7e59be1928cbca893ecb';

const RaptorMap: React.FC<RaptorMapProps> = memo(
  ({
    config = {},
    style = DEFAULT_STYLE,
    onZoomChanged,
    onCenterChanged,
    onClick,
    onContextmenu,
    children,
    setMapInstance,
    markers = [],
    enableClustering = true,
    handleMapInteractions,
    showControls = true,
    clusteringOptions,
    showGrid = false
  }) => {
    const map = useMap();
    const { handleMarkerClick } = useMapInteractions(handleMapInteractions);

    // Use internal state management
    const {
      mapState,
      handleZoomIn,
      handleZoomOut,
      handleMapTypeChange,
      handleReset,
      handleZoomChanged: internalHandleZoomChanged,
      handleCenterChanged: internalHandleCenterChanged
    } = useMapState();

    useEffect(() => {
      map && setMapInstance?.(map);
    }, [map, setMapInstance]);

    const handleCameraChanged = (ev: MapCameraChangedEvent) => {
      const newZoom = ev.detail.zoom;
      const newCenter = {
        lat: ev.detail.center.lat,
        lng: ev.detail.center.lng
      };

      // Update internal state
      internalHandleZoomChanged(newZoom);
      internalHandleCenterChanged(newCenter);

      // Call external callbacks if provided
      onZoomChanged?.(newZoom);
      onCenterChanged?.(newCenter);
    };

    const renderMarkers = () => {
      if (enableClustering && map) {
        return (
          <ClusterController
            key="cluster-controller"
            markers={markers}
            map={map}
            onClusterClick={undefined} // Remove the handler that was opening details panels
            onMarkerClick={(_, featureId) => handleMarkerClick(featureId)} // Handle individual marker clicks
            clusteringOptions={clusteringOptions}
          />
        );
      }

      if (!markers.length) return null;

      return markers.map((marker) => (
        <AdvancedMarker
          key={marker.id}
          position={marker.position}
          title={marker.title}
          onClick={() => handleMarkerClick(String(marker.id))}
          draggable={marker.draggable}
          zIndex={marker.zIndex}
        >
          {marker.children}
        </AdvancedMarker>
      ));
    };

    // Merge external config with internal state
    const finalConfig = {
      ...mapState,
      ...config
    };

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <GoogleMapComponent
          mapId={MAP_ID}
          style={style}
          center={finalConfig.center}
          zoom={finalConfig.zoom}
          minZoom={MAP_CONFIG.MIN_ZOOM}
          gestureHandling={finalConfig.gestureHandling}
          mapTypeId={finalConfig.mapType}
          onCameraChanged={handleCameraChanged}
          disableDefaultUI={finalConfig.disableDefaultUI}
          onClick={onClick}
          onContextmenu={onContextmenu}
        >
          {renderMarkers()}
          {showGrid && <ClusteringGridOverlay clusteringOptions={clusteringOptions} visible={showGrid} />}
          {children}
        </GoogleMapComponent>

        {showControls && (
          <MapControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            mapType={mapState.mapType}
            onMapTypeChange={handleMapTypeChange}
            onReset={handleReset}
          />
        )}
      </div>
    );
  }
);

RaptorMap.displayName = 'RaptorMap';

export default RaptorMap;
